/**
 * DataGrid — the desktop (`lg`+) tabular view, built on TanStack Table.
 *
 * Reflow contract (hard rule): below `lg` this component renders the supplied
 * CardList instead of a table, so a tablet never scrolls the body horizontally.
 * At `lg`+ it renders a real table; if that table is ever wider than its
 * container it scrolls INSIDE its own `overflow-x-auto` wrapper — never the page.
 *
 * Generic over the row type; columns are TanStack ColumnDef. The caller passes a
 * `renderCard` used for the sub-lg reflow, keeping one data source for both.
 *
 * Long lists (`pageSize`, added 2026-10-07 for the customer Overview at 100+ sites): the grid
 * renders the first `pageSize` rows of the SORTED set and a "Show N more" control below.
 * Paging happens after sorting, never before, so a column sort always orders the whole list
 * rather than just the visible slice. Incremental "show more" rather than numbered pages
 * because this is a scan-and-triage list on a tablet: the operator keeps their place and
 * scroll position while extending it.
 */
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { useEffect, useState, type ReactNode } from "react";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import { CardList } from "./CardList";
import { cn } from "@/lib/cn";

interface DataGridProps<T> {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  rowKey: (row: T) => string;
  /** Card renderer for the sub-`lg` reflow. */
  renderCard: (row: T) => ReactNode;
  onRowActivate?: (row: T) => void;
  empty?: ReactNode;
  isLoading?: boolean;
  /**
   * Sort the grid starts on. Uncontrolled after mount — the user's first click takes over.
   *
   * A list's DEFAULT order is an editorial decision, not a neutral one: whatever is at the
   * top is what gets looked at. The device list opens on recency for exactly this reason, so
   * the devices that have stopped checking in surface without anyone applying a filter.
   */
  initialSorting?: SortingState;
  /**
   * false = always render the table, scrolling horizontally INSIDE its own wrapper below `lg`
   * instead of reflowing to cards. For dense operational grids where the columns ARE the
   * content and a card per row would bury the comparison (the switch port table: 28-54 rows
   * per switch, read across). The page body still never scrolls horizontally. Default true.
   */
  reflow?: boolean;
  /** Tighter cell padding for long, scan-read tables. */
  dense?: boolean;
  /** Extra classes per row (e.g. a selected-row tint). */
  rowClassName?: (row: T) => string | false | null | undefined;
  /**
   * Render at most this many rows (after sorting), with a "Show more" control for the rest.
   * Omit for the old behaviour (render every row).
   */
  pageSize?: number;
  /**
   * When this value changes the visible window snaps back to `pageSize` -- pass the caller's
   * search/filter state so a new query starts at the top. Deliberately NOT keyed on `data`:
   * a background refetch hands a new array every poll and must not collapse the list the
   * operator just expanded.
   */
  pageResetKey?: string;
}

export function DataGrid<T>({
  data,
  columns,
  rowKey,
  renderCard,
  onRowActivate,
  empty,
  isLoading,
  initialSorting,
  reflow = true,
  dense = false,
  rowClassName,
  pageSize,
  pageResetKey,
}: DataGridProps<T>) {
  const isDesktop = useIsDesktop();
  const cellPad = dense ? "px-3 py-1.5" : "px-4 py-3";
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  // Visible-row window for `pageSize` mode (Infinity = render all, the default behaviour).
  const [limit, setLimit] = useState<number>(pageSize ?? Infinity);
  useEffect(() => {
    setLimit(pageSize ?? Infinity);
  }, [pageResetKey, pageSize]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => rowKey(row),
  });

  if (isLoading) {
    return <GridSkeleton />;
  }

  // Sorted rows, then the visible window. Without pageSize this is every row, unchanged.
  const sortedRows = table.getRowModel().rows;
  const visibleRows = pageSize ? sortedRows.slice(0, limit) : sortedRows;
  const hidden = sortedRows.length - visibleRows.length;
  const more =
    pageSize && hidden > 0 ? (
      <ShowMore
        shown={visibleRows.length}
        total={sortedRows.length}
        step={Math.min(pageSize, hidden)}
        onMore={() => setLimit((n) => n + pageSize)}
        onAll={() => setLimit(Infinity)}
      />
    ) : null;

  // --- Sub-lg: reflow to cards (NO horizontal scroll on tablet) ---
  if (!isDesktop && reflow) {
    return (
      <>
        <CardList
          // In pageSize mode the cards follow the same sorted window as the table; otherwise
          // the caller's order, exactly as before.
          rows={pageSize ? visibleRows.map((r) => r.original) : data}
          rowKey={rowKey}
          renderCard={renderCard}
          onRowActivate={onRowActivate}
          empty={empty}
        />
        {more}
      </>
    );
  }

  // --- lg+: real table, horizontally scrollable within its own wrapper only ---
  return (
    <div className="md-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-line">
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  return (
                    <th
                      key={header.id}
                      className={cn(cellPad, "whitespace-nowrap text-left font-medium text-ink-muted")}
                    >
                      {canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 hover:text-ink"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <SortGlyph dir={header.column.getIsSorted()} />
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-14 text-center">
                  {/* A composed empty row: the neutral tray glyph in a soft disc over the caller's
                      message. Not an md-card (it already sits inside the grid's card) — just a
                      centred, roomier treatment so an empty result reads as a state, not a gap. */}
                  <div className="md-enter mx-auto flex max-w-md flex-col items-center">
                    <span
                      aria-hidden
                      className="mb-3 grid h-12 w-12 place-items-center rounded-full bg-surface-2 text-ink-subtle"
                    >
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 13h4l2 3h6l2-3h4" />
                        <path d="M5 13V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v7" />
                      </svg>
                    </span>
                    <div className="text-sm text-ink-muted">{empty ?? "Nothing here yet."}</div>
                  </div>
                </td>
              </tr>
            ) : (
              visibleRows.map((row) => (
                <tr
                  key={row.id}
                  onClick={onRowActivate ? () => onRowActivate(row.original) : undefined}
                  className={cn(
                    "border-b border-line last:border-0",
                    onRowActivate && "cursor-pointer hover:bg-surface-2",
                    rowClassName?.(row.original),
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={cn(cellPad, "text-ink")}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {more}
    </div>
  );
}

/** The "Showing 50 of 230 · Show 50 more · Show all" footer for pageSize mode. */
function ShowMore({
  shown,
  total,
  step,
  onMore,
  onAll,
}: {
  shown: number;
  total: number;
  step: number;
  onMore: () => void;
  onAll: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-2 text-sm">
      <span className="tabular-nums text-ink-muted">
        Showing {shown} of {total}
      </span>
      <span className="flex items-center gap-1">
        {/* Interactive affordances: brand ink, 44px touch floor. */}
        <button
          type="button"
          onClick={onMore}
          className="min-h-touch rounded px-3 font-medium text-brand hover:bg-brand-soft"
        >
          Show {step} more
        </button>
        <button
          type="button"
          onClick={onAll}
          className="min-h-touch rounded px-3 text-ink-muted hover:bg-surface-2"
        >
          Show all
        </button>
      </span>
    </div>
  );
}

function SortGlyph({ dir }: { dir: false | "asc" | "desc" }) {
  return (
    <span aria-hidden className="text-ink-subtle">
      {dir === "asc" ? "↑" : dir === "desc" ? "↓" : "↕"}
    </span>
  );
}

/**
 * Loading placeholder for the grid. A header strip plus staggered body rows, each bar a
 * `.md-skeleton` shimmer (see global.css) rather than the old flat pulse — a directional sweep
 * reads as "loading" more clearly and the varied column widths echo the real table beneath it,
 * so the transition into live data is a settle, not a swap. Reduced-motion leaves static bars.
 */
function GridSkeleton() {
  // Per-row column widths, varied so the placeholder looks like a table and not a progress bar.
  const COLS = ["w-1/4", "w-1/3", "w-1/6", "w-1/5"];
  return (
    <div className="md-card overflow-hidden" aria-busy aria-live="polite">
      <span className="sr-only">Loading…</span>
      {/* header strip */}
      <div className="flex items-center gap-4 border-b border-line px-4 py-3">
        {COLS.map((w, i) => (
          <div key={i} className={`md-skeleton h-3 rounded ${w}`} />
        ))}
      </div>
      {/* body rows */}
      <div className="divide-y divide-line">
        {Array.from({ length: 6 }).map((_, r) => (
          <div key={r} className="flex items-center gap-4 px-4 py-3.5">
            {COLS.map((w, c) => (
              <div
                key={c}
                className={`md-skeleton h-4 rounded ${w}`}
                // Each cell shimmers a beat after the one before it, left-to-right then top-down,
                // so the whole grid breathes as one surface instead of 24 twitching blocks. The
                // delay feeds the shimmer pseudo-element via the --sk-delay custom property.
                style={{ ["--sk-delay" as never]: `${(r * COLS.length + c) * 60}ms` }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
